import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Calendar, Key, MessageSquare, FileText, Plus, Trash2, Heart,
  Eye, Search, Send, ChevronLeft, ChevronRight, Download,
  X, Edit3, Check, Loader2, Lock, Globe, User, EyeOff,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardHeader } from "@/components/DashboardHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CITY_COLORS = {
  "København":  "#FF2A6D",
  "Koebenhavn": "#FF2A6D",
  "Aarhus":     "#3B82F6",
  "Odense":     "#F59E0B",
  "Aalborg":    "#10B981",
  "Kolding":    "#8B5CF6",
  "Vejle":      "#F97316",
};

const CITIES = ["København", "Aarhus", "Odense", "Aalborg", "Kolding", "Vejle"];

const EVENT_TYPES = [
  "Eksamen",
  "Hold start",
  "Lash og Brow kursus",
  "Korean lash lift kursus",
  "Hotstone kursus",
  "TNBC besøg",
  "Studievejleder besøg",
  "Andet",
];

const MONTH_NAMES = ["Januar", "Februar", "Marts", "April", "Maj", "Juni",
  "Juli", "August", "September", "Oktober", "November", "December"];
const DAY_NAMES = ["Ma", "Ti", "On", "To", "Fr", "Lø", "Sø"];

function fmtTs(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    return d.toLocaleDateString("da-DK", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ts; }
}

function fmtBytes(n) {
  if (!n) return "";
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(0) + " KB";
  return (n / (1024 * 1024)).toFixed(1) + " MB";
}

function fileIcon(ct = "") {
  if (ct.includes("pdf")) return "📄";
  if (ct.includes("word") || ct.includes("doc")) return "📝";
  if (ct.includes("sheet") || ct.includes("excel") || ct.includes("csv")) return "📊";
  if (ct.includes("image")) return "🖼";
  return "📎";
}

// ── Kalender ──────────────────────────────────────────────────────────────────

function KalenderTab({ token }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [events, setEvents] = useState([]);
  const [cityColors, setCityColors] = useState(CITY_COLORS);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [newEvent, setNewEvent] = useState({ event_type: "Eksamen", custom_title: "", city: "København", day: 1 });
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/hub/kalender?year=${year}&month=${month}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => {
      setEvents(r.data.events || []);
      setCityColors(r.data.city_colors || CITY_COLORS);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [token, year, month]);

  useEffect(() => { load(); }, [load]);

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };

  // Byg kalender-grid
  const firstDay = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  // Mandag = 0, ..., Søndag = 6
  let startDow = firstDay.getDay() - 1;
  if (startDow < 0) startDow = 6;

  const days = [];
  for (let i = 0; i < startDow; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  // Events per dag
  const eventsByDay = {};
  for (const ev of events) {
    const d = ev.day;
    if (!eventsByDay[d]) eventsByDay[d] = [];
    eventsByDay[d].push(ev);
  }

  const handleAddEvent = async () => {
    if (!newEvent.day || !newEvent.event_type) return;
    setAdding(true);
    try {
      const title = newEvent.event_type === "Andet" ? newEvent.custom_title : newEvent.event_type;
      await axios.post(`${API}/hub/kalender`, {
        year, month,
        day: newEvent.day,
        event_type: newEvent.event_type,
        custom_title: newEvent.custom_title,
        title,
        city: newEvent.city,
        color: CITY_COLORS[newEvent.city] || "#6B7280",
      }, { headers: { Authorization: `Bearer ${token}` } });
      setShowAdd(false);
      setNewEvent({ event_type: "Eksamen", custom_title: "", city: "København", day: 1 });
      load();
    } catch { }
    setAdding(false);
  };

  const handleDelete = async (event_id) => {
    await axios.delete(`${API}/hub/kalender/${event_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setEvents(prev => prev.filter(e => e.event_id !== event_id));
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-secondary transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <h2 className="text-base font-bold">{MONTH_NAMES[month - 1]} {year}</h2>
          <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-secondary transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FF2A6D] text-white text-[13px] font-semibold hover:opacity-90 transition-opacity"
        >
          <Plus className="w-3.5 h-3.5" /> Tilføj begivenhed
        </button>
      </div>

      {/* By-farve-forklaring */}
      <div className="flex flex-wrap gap-2">
        {CITIES.map(city => (
          <span key={city} className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: CITY_COLORS[city] }} />
            {city}
          </span>
        ))}
      </div>

      {/* Kalender-grid */}
      {loading ? (
        <div className="text-muted-foreground text-sm py-8 text-center">Henter kalender...</div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          {/* Dage-header */}
          <div className="grid grid-cols-7 border-b border-border">
            {DAY_NAMES.map(d => (
              <div key={d} className="text-center text-[13px] font-semibold text-muted-foreground py-2 bg-secondary/30">{d}</div>
            ))}
          </div>
          {/* Dage */}
          <div className="grid grid-cols-7">
            {days.map((day, i) => {
              const dayEvents = day ? (eventsByDay[day] || []) : [];
              const isToday = day && year === now.getFullYear() && month === now.getMonth() + 1 && day === now.getDate();
              return (
                <div
                  key={i}
                  className={`min-h-[90px] border-b border-r border-border p-1.5 ${day ? "hover:bg-secondary/20 transition-colors" : "bg-secondary/5"}`}
                  onClick={() => day && setSelectedDay(selectedDay === day ? null : day)}
                >
                  {day && (
                    <>
                      <div className={`text-[13px] font-semibold mb-1 w-6 h-6 flex items-center justify-center rounded-full
                        ${isToday ? "bg-[#FF2A6D] text-white" : "text-foreground"}`}>
                        {day}
                      </div>
                      <div className="space-y-0.5">
                        {dayEvents.slice(0, 3).map((ev, idx) => (
                          <div
                            key={idx}
                            className="text-[13px] leading-tight rounded px-1 py-0.5 truncate font-medium"
                            style={{ backgroundColor: (ev.color || "#6B7280") + "20", color: ev.color || "#6B7280" }}
                          >
                            {ev.title}
                          </div>
                        ))}
                        {dayEvents.length > 3 && (
                          <div className="text-[13px] text-muted-foreground px-1">+{dayEvents.length - 3} mere</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Dag-detalje */}
      {selectedDay && eventsByDay[selectedDay] && eventsByDay[selectedDay].length > 0 && (
        <div className="rounded-xl border border-border p-4 space-y-2">
          <h3 className="text-sm font-bold">{selectedDay}. {MONTH_NAMES[month - 1]}</h3>
          {eventsByDay[selectedDay].map((ev, i) => (
            <div key={i} className="flex items-center justify-between gap-3 p-2.5 rounded-lg" style={{ backgroundColor: (ev.color || "#6B7280") + "12" }}>
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: ev.color || "#6B7280" }} />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{ev.title}</p>
                  {ev.city && <p className="text-[13px] text-muted-foreground">{ev.city}</p>}
                </div>
              </div>
              {ev.event_id && (
                <button
                  onClick={() => handleDelete(ev.event_id)}
                  className="p-1 rounded text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors shrink-0"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Tilføj-modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-background border border-border rounded-xl shadow-xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm">Ny begivenhed</h3>
              <button onClick={() => setShowAdd(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">Dag i {MONTH_NAMES[month - 1]}</label>
                <input
                  type="number"
                  min={1} max={daysInMonth}
                  value={newEvent.day}
                  onChange={e => setNewEvent(n => ({ ...n, day: parseInt(e.target.value) || 1 }))}
                  className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
                />
              </div>
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">Type</label>
                <select
                  value={newEvent.event_type}
                  onChange={e => setNewEvent(n => ({ ...n, event_type: e.target.value }))}
                  className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none"
                >
                  {EVENT_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              {newEvent.event_type === "Andet" && (
                <div>
                  <label className="text-[13px] text-muted-foreground mb-1 block">Titel</label>
                  <input
                    type="text"
                    placeholder="Beskriv begivenheden..."
                    value={newEvent.custom_title}
                    onChange={e => setNewEvent(n => ({ ...n, custom_title: e.target.value }))}
                    className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
                  />
                </div>
              )}
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">By (farve)</label>
                <div className="flex flex-wrap gap-1.5">
                  {CITIES.map(city => (
                    <button
                      key={city}
                      onClick={() => setNewEvent(n => ({ ...n, city }))}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[13px] font-semibold border transition-colors
                        ${newEvent.city === city ? "border-transparent text-white" : "border-border text-muted-foreground hover:text-foreground"}`}
                      style={newEvent.city === city ? { backgroundColor: CITY_COLORS[city] } : {}}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: CITY_COLORS[city] }} />
                      {city}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary transition-colors">Annuller</button>
              <button
                onClick={handleAddEvent}
                disabled={adding || (newEvent.event_type === "Andet" && !newEvent.custom_title.trim())}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : "Tilføj"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Koder ─────────────────────────────────────────────────────────────────────

function KoderTab({ token }) {
  const [koder, setKoder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newKode, setNewKode] = useState({ titel: "", domæne: "", brugernavn: "", kode: "", noter: "" });
  const [adding, setAdding] = useState(false);
  const [visible, setVisible] = useState({});
  const [editing, setEditing] = useState(null); // kode_id

  useEffect(() => {
    axios.get(`${API}/hub/koder`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setKoder(r.data.koder || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const handleAdd = async () => {
    if (!newKode.titel.trim()) return;
    setAdding(true);
    try {
      const r = await axios.post(`${API}/hub/koder`, newKode, { headers: { Authorization: `Bearer ${token}` } });
      setKoder(prev => [...prev, r.data]);
      setNewKode({ titel: "", domæne: "", brugernavn: "", kode: "", noter: "" });
      setShowAdd(false);
    } catch { }
    setAdding(false);
  };

  const handleDelete = async (kode_id) => {
    if (!window.confirm("Slet denne kode?")) return;
    await axios.delete(`${API}/hub/koder/${kode_id}`, { headers: { Authorization: `Bearer ${token}` } });
    setKoder(prev => prev.filter(k => k.kode_id !== kode_id));
  };

  const handleUpdate = async (kode_id, update) => {
    await axios.put(`${API}/hub/koder/${kode_id}`, update, { headers: { Authorization: `Bearer ${token}` } });
    setKoder(prev => prev.map(k => k.kode_id === kode_id ? { ...k, ...update } : k));
    setEditing(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text).catch(() => {});
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Vigtige koder og logins</p>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FF2A6D] text-white text-[13px] font-semibold hover:opacity-90 transition-opacity"
        >
          <Plus className="w-3.5 h-3.5" /> Tilføj kode
        </button>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm py-8 text-center">Henter koder...</div>
      ) : koder.length === 0 ? (
        <div className="text-muted-foreground text-sm py-8 text-center">Ingen koder endnu</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {koder.map(k => (
            <KodeKort
              key={k.kode_id}
              kode={k}
              isVisible={!!visible[k.kode_id]}
              onToggleVisible={() => setVisible(v => ({ ...v, [k.kode_id]: !v[k.kode_id] }))}
              onDelete={() => handleDelete(k.kode_id)}
              onCopy={copyToClipboard}
              editing={editing === k.kode_id}
              onEdit={() => setEditing(k.kode_id)}
              onSave={(update) => handleUpdate(k.kode_id, update)}
              onCancelEdit={() => setEditing(null)}
            />
          ))}
        </div>
      )}

      {/* Tilføj-modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-background border border-border rounded-xl shadow-xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm">Ny kode / login</h3>
              <button onClick={() => setShowAdd(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              {[["titel", "Overskrift *"], ["domæne", "Domæne / URL"], ["brugernavn", "Brugernavn"], ["kode", "Kode"], ["noter", "Noter"]].map(([key, label]) => (
                <div key={key}>
                  <label className="text-[13px] text-muted-foreground mb-1 block">{label}</label>
                  <input
                    type={key === "kode" ? "text" : "text"}
                    value={newKode[key]}
                    onChange={e => setNewKode(n => ({ ...n, [key]: e.target.value }))}
                    className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary transition-colors">Annuller</button>
              <button
                onClick={handleAdd}
                disabled={adding || !newKode.titel.trim()}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : "Gem"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KodeKort({ kode, isVisible, onToggleVisible, onDelete, onCopy, editing, onEdit, onSave, onCancelEdit }) {
  const [draft, setDraft] = useState({ ...kode });

  useEffect(() => { setDraft({ ...kode }); }, [kode, editing]);

  if (editing) {
    return (
      <div className="rounded-xl border border-[#FF2A6D] p-4 space-y-2 bg-background">
        {[["titel", "Overskrift"], ["domæne", "Domæne"], ["brugernavn", "Brugernavn"], ["kode", "Kode"], ["noter", "Noter"]].map(([key, label]) => (
          <div key={key}>
            <label className="text-[13px] text-muted-foreground mb-0.5 block">{label}</label>
            <input
              type="text"
              value={draft[key] || ""}
              onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
              className="w-full bg-secondary border border-border rounded-lg px-2 h-8 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
            />
          </div>
        ))}
        <div className="flex gap-2 justify-end pt-1">
          <button onClick={onCancelEdit} className="px-3 py-1 text-[13px] border border-border rounded-lg hover:bg-secondary transition-colors">Annuller</button>
          <button onClick={() => onSave(draft)} className="px-3 py-1 text-[13px] bg-[#FF2A6D] text-white rounded-lg hover:opacity-90 transition-opacity">Gem</button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border p-4 space-y-2.5 group hover:border-border/80 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Lock className="w-3.5 h-3.5 text-[#FF2A6D] shrink-0" />
          <span className="font-semibold text-sm truncate">{kode.titel}</span>
        </div>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <button onClick={onEdit} className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
            <Edit3 className="w-3 h-3" />
          </button>
          <button onClick={onDelete} className="p-1 rounded text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>

      {kode.domæne && (
        <div className="flex items-center gap-1.5">
          <Globe className="w-3 h-3 text-muted-foreground shrink-0" />
          <span className="text-[13px] text-muted-foreground truncate">{kode.domæne}</span>
        </div>
      )}
      {kode.brugernavn && (
        <div>
          <p className="text-[13px] text-muted-foreground mb-0.5">Brugernavn</p>
          <button
            onClick={() => onCopy(kode.brugernavn)}
            className="text-[13px] font-mono hover:text-[#FF2A6D] transition-colors"
            title="Klik for at kopiere"
          >
            {kode.brugernavn}
          </button>
        </div>
      )}
      {kode.kode && (
        <div>
          <p className="text-[13px] text-muted-foreground mb-0.5">Kode</p>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => onCopy(kode.kode)}
              className="text-[13px] font-mono hover:text-[#FF2A6D] transition-colors flex-1 text-left"
              title="Klik for at kopiere"
            >
              {isVisible ? kode.kode : "••••••••"}
            </button>
            <button onClick={onToggleVisible} className="p-0.5 text-muted-foreground hover:text-foreground transition-colors">
              {isVisible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      )}
      {kode.noter && (
        <p className="text-[13px] text-muted-foreground border-t border-border pt-2 mt-1">{kode.noter}</p>
      )}
    </div>
  );
}


// ── Feed ─────────────────────────────────────────────────────────────────────

function FeedTab({ token, currentUser }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newPost, setNewPost] = useState({ titel: "", tekst: "" });
  const [posting, setPosting] = useState(false);
  const [openComments, setOpenComments] = useState({});
  const [commentDraft, setCommentDraft] = useState({});
  const [sendingComment, setSendingComment] = useState({});
  const [showSeen, setShowSeen] = useState({});
  const searchTimeout = useRef(null);

  const load = useCallback((q = "") => {
    const params = q ? `?search=${encodeURIComponent(q)}` : "";
    axios.get(`${API}/hub/feed${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPosts(r.data.posts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (q) => {
    setSearch(q);
    clearTimeout(searchTimeout.current);
    setSearching(true);
    searchTimeout.current = setTimeout(() => {
      load(q);
      setSearching(false);
    }, 400);
  };

  const handlePost = async () => {
    if (!newPost.tekst.trim()) return;
    setPosting(true);
    try {
      const r = await axios.post(`${API}/hub/feed`, newPost, { headers: { Authorization: `Bearer ${token}` } });
      setPosts(prev => [r.data, ...prev]);
      setNewPost({ titel: "", tekst: "" });
      setShowNew(false);
    } catch { }
    setPosting(false);
  };

  const handleLike = async (post_id) => {
    const r = await axios.post(`${API}/hub/feed/${post_id}/like`, {}, { headers: { Authorization: `Bearer ${token}` } });
    setPosts(prev => prev.map(p => p.post_id === post_id ? {
      ...p,
      liked_by_me: r.data.liked,
      likes: r.data.liked
        ? [...(p.likes || []), currentUser]
        : (p.likes || []).filter(n => n !== currentUser),
    } : p));
  };

  const handleSeen = async (post_id) => {
    await axios.post(`${API}/hub/feed/${post_id}/seen`, {}, { headers: { Authorization: `Bearer ${token}` } });
    setPosts(prev => prev.map(p => p.post_id === post_id ? {
      ...p,
      seen_by_me: true,
      seen_by: [...new Set([...(p.seen_by || []), currentUser])],
    } : p));
  };

  const handleComment = async (post_id) => {
    const tekst = (commentDraft[post_id] || "").trim();
    if (!tekst) return;
    setSendingComment(s => ({ ...s, [post_id]: true }));
    try {
      const r = await axios.post(`${API}/hub/feed/${post_id}/kommentar`, { tekst }, { headers: { Authorization: `Bearer ${token}` } });
      setPosts(prev => prev.map(p => p.post_id === post_id ? {
        ...p,
        kommentarer: [...(p.kommentarer || []), r.data],
      } : p));
      setCommentDraft(d => ({ ...d, [post_id]: "" }));
    } catch { }
    setSendingComment(s => ({ ...s, [post_id]: false }));
  };

  const handleDeletePost = async (post_id) => {
    if (!window.confirm("Slet dette opslag?")) return;
    await axios.delete(`${API}/hub/feed/${post_id}`, { headers: { Authorization: `Bearer ${token}` } });
    setPosts(prev => prev.filter(p => p.post_id !== post_id));
  };

  const handleDeleteComment = async (post_id, kommentar_id) => {
    await axios.delete(`${API}/hub/feed/${post_id}/kommentar/${kommentar_id}`, { headers: { Authorization: `Bearer ${token}` } });
    setPosts(prev => prev.map(p => p.post_id === post_id ? {
      ...p,
      kommentarer: (p.kommentarer || []).filter(k => k.kommentar_id !== kommentar_id),
    } : p));
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Søg i feed..."
            value={search}
            onChange={e => handleSearch(e.target.value)}
            className="w-full bg-secondary border border-border rounded-lg pl-9 pr-3 h-9 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
          />
          {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-muted-foreground" />}
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FF2A6D] text-white text-[13px] font-semibold hover:opacity-90 transition-opacity shrink-0"
        >
          <Plus className="w-3.5 h-3.5" /> Nyt opslag
        </button>
      </div>

      {/* Nyt opslag modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-background border border-border rounded-xl shadow-xl p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm">Nyt opslag</h3>
              <button onClick={() => setShowNew(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Overskrift (valgfri)"
                value={newPost.titel}
                onChange={e => setNewPost(n => ({ ...n, titel: e.target.value }))}
                className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
              />
              <textarea
                placeholder="Hvad vil du dele?"
                value={newPost.tekst}
                onChange={e => setNewPost(n => ({ ...n, tekst: e.target.value }))}
                rows={5}
                className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] resize-y"
              />
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setShowNew(false)} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary transition-colors">Annuller</button>
              <button
                onClick={handlePost}
                disabled={posting || !newPost.tekst.trim()}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Del opslag"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feed */}
      {loading ? (
        <div className="text-muted-foreground text-sm py-8 text-center">Henter feed...</div>
      ) : posts.length === 0 ? (
        <div className="text-muted-foreground text-sm py-8 text-center">
          {search ? "Ingen opslag matcher søgningen" : "Ingen opslag endnu"}
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map(post => (
            <FeedPost
              key={post.post_id}
              post={post}
              currentUser={currentUser}
              token={token}
              openComments={!!openComments[post.post_id]}
              onToggleComments={() => {
                setOpenComments(o => ({ ...o, [post.post_id]: !o[post.post_id] }));
                if (!post.seen_by_me) handleSeen(post.post_id);
              }}
              onLike={() => handleLike(post.post_id)}
              showSeen={!!showSeen[post.post_id]}
              onToggleSeen={() => setShowSeen(s => ({ ...s, [post.post_id]: !s[post.post_id] }))}
              commentDraft={commentDraft[post.post_id] || ""}
              onCommentChange={v => setCommentDraft(d => ({ ...d, [post.post_id]: v }))}
              onCommentSubmit={() => handleComment(post.post_id)}
              sendingComment={!!sendingComment[post.post_id]}
              onDelete={() => handleDeletePost(post.post_id)}
              onDeleteComment={(kid) => handleDeleteComment(post.post_id, kid)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FeedPost({ post, currentUser, openComments, onToggleComments, onLike, showSeen, onToggleSeen,
  commentDraft, onCommentChange, onCommentSubmit, sendingComment, onDelete, onDeleteComment }) {
  return (
    <div className="rounded-xl border border-border overflow-hidden">
      {/* Post-header */}
      <div className="p-4 pb-3">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center shrink-0">
              <User className="w-3.5 h-3.5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-[13px] font-semibold">{post.created_by}</p>
              <p className="text-[13px] text-muted-foreground">{fmtTs(post.created_at)}</p>
            </div>
          </div>
          <button onClick={onDelete} className="p-1 rounded text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>

        {post.titel && <h3 className="font-semibold text-sm mb-1">{post.titel}</h3>}
        <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{post.tekst}</p>
      </div>

      {/* Reaktioner */}
      <div className="flex items-center gap-1 px-4 py-2 border-t border-border bg-secondary/20">
        <button
          onClick={onLike}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[13px] font-medium transition-colors
            ${post.liked_by_me ? "text-red-500 bg-red-500/10" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}`}
        >
          <Heart className={`w-3.5 h-3.5 ${post.liked_by_me ? "fill-current" : ""}`} />
          {(post.likes || []).length > 0 && <span>{(post.likes || []).length}</span>}
        </button>

        <button
          onClick={onToggleComments}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[13px] font-medium transition-colors
            ${openComments ? "text-blue-500 bg-blue-500/10" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}`}
        >
          <MessageSquare className="w-3.5 h-3.5" />
          {(post.kommentarer || []).length > 0 && <span>{(post.kommentarer || []).length}</span>}
        </button>

        <button
          onClick={onToggleSeen}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[13px] font-medium transition-colors
            ${post.seen_by_me ? "text-green-600 dark:text-green-400 bg-green-500/10" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}`}
        >
          <Eye className="w-3.5 h-3.5" />
          {(post.seen_by || []).length > 0 && <span>{(post.seen_by || []).length}</span>}
        </button>
      </div>

      {/* Set-af liste */}
      {showSeen && (post.seen_by || []).length > 0 && (
        <div className="px-4 py-2 border-t border-border bg-secondary/10">
          <p className="text-[13px] text-muted-foreground font-medium mb-1">Set af:</p>
          <div className="flex flex-wrap gap-1">
            {(post.seen_by || []).map(name => (
              <span key={name} className="text-[13px] bg-secondary px-2 py-0.5 rounded-full">{name}</span>
            ))}
          </div>
        </div>
      )}

      {/* Likes liste */}
      {(post.likes || []).length > 0 && (
        <div className="px-4 py-1.5 border-t border-border">
          <p className="text-[13px] text-muted-foreground">
            <Heart className="w-2.5 h-2.5 inline fill-red-500 text-red-500 mr-1" />
            {(post.likes || []).join(", ")}
          </p>
        </div>
      )}

      {/* Kommentarer */}
      {openComments && (
        <div className="border-t border-border">
          {(post.kommentarer || []).map(k => (
            <div key={k.kommentar_id} className="flex items-start gap-2.5 px-4 py-2.5 border-b border-border/50 last:border-0 group">
              <div className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center shrink-0 mt-0.5">
                <User className="w-3 h-3 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold">{k.created_by} <span className="font-normal text-muted-foreground">· {fmtTs(k.created_at)}</span></p>
                <p className="text-sm">{k.tekst}</p>
              </div>
              <button
                onClick={() => onDeleteComment(k.kommentar_id)}
                className="p-1 rounded text-muted-foreground hover:text-red-500 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {/* Ny kommentar */}
          <div className="flex items-center gap-2 px-4 py-2.5 bg-secondary/10">
            <input
              type="text"
              placeholder="Skriv en kommentar..."
              value={commentDraft}
              onChange={e => onCommentChange(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onCommentSubmit(); } }}
              className="flex-1 bg-background border border-border rounded-lg px-3 h-8 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
            />
            <button
              onClick={onCommentSubmit}
              disabled={sendingComment || !commentDraft.trim()}
              className="p-2 rounded-lg bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {sendingComment ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Dokumenter ────────────────────────────────────────────────────────────────

function DokumenterTab({ token }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [navnModal, setNavnModal] = useState(null); // { file }
  const [navnDraft, setNavnDraft] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/hub/dokumenter`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setDocs(r.data.dokumenter || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNavnDraft(file.name);
    setNavnModal({ file });
    e.target.value = "";
  };

  const handleUpload = async () => {
    if (!navnModal?.file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", navnModal.file);
      fd.append("navn", navnDraft.trim() || navnModal.file.name);
      const r = await axios.post(`${API}/hub/dokumenter`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setDocs(prev => [r.data, ...prev]);
      setNavnModal(null);
      setNavnDraft("");
    } catch { alert("Upload fejlede"); }
    setUploading(false);
  };

  const handleDelete = async (doc_id, filename) => {
    if (!window.confirm(`Slet "${filename}"?`)) return;
    await axios.delete(`${API}/hub/dokumenter/${doc_id}`, { headers: { Authorization: `Bearer ${token}` } });
    setDocs(prev => prev.filter(d => d.doc_id !== doc_id));
  };

  const handleDownload = async (doc_id, filename, ct) => {
    const r = await fetch(`${API}/hub/dokumenter/${doc_id}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Delte dokumenter for alle administratorer</p>
        <div>
          <input ref={fileRef} type="file" className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif,.webp"
            onChange={handleFileSelect}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FF2A6D] text-white text-[13px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Upload dokument
          </button>
        </div>
      </div>

      {/* Navn-modal */}
      {navnModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-background border border-border rounded-xl shadow-xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm">Navngiv dokumentet</h3>
              <button onClick={() => setNavnModal(null)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <input
              type="text"
              value={navnDraft}
              onChange={e => setNavnDraft(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleUpload()}
              autoFocus
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm mb-4 focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setNavnModal(null)} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary transition-colors">Annuller</button>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-muted-foreground text-sm py-8 text-center">Henter dokumenter...</div>
      ) : docs.length === 0 ? (
        <div className="text-muted-foreground text-sm py-8 text-center">Ingen dokumenter endnu</div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          {docs.map((doc, i) => (
            <div key={doc.doc_id} className={`flex items-center gap-3 px-4 py-3 group hover:bg-secondary/20 transition-colors ${i > 0 ? "border-t border-border" : ""}`}>
              <span className="text-xl shrink-0">{fileIcon(doc.content_type)}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{doc.filename}</p>
                <p className="text-[13px] text-muted-foreground">{fmtBytes(doc.size)} · {doc.uploaded_by} · {fmtTs(doc.uploaded_at)}</p>
              </div>
              <button
                onClick={() => handleDownload(doc.doc_id, doc.filename, doc.content_type)}
                className="p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                title="Download"
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDelete(doc.doc_id, doc.filename)}
                className="p-1.5 rounded-full text-muted-foreground hover:text-red-500 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Hoved-komponent ───────────────────────────────────────────────────────────

const TABS = [
  { id: "kalender", label: "Kalender", icon: Calendar },
  { id: "feed",     label: "Feed",     icon: MessageSquare },
  { id: "koder",    label: "Koder",    icon: Key },
  { id: "filer",    label: "Filer",    icon: FileText },
];

export default function HubPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("feed");

  const handleLogout = () => { logout(); navigate("/login"); };

  useEffect(() => {
    if (!user || !["superadmin", "owner", "api_admin"].includes(user.role)) {
      navigate("/");
    }
  }, [user, navigate]);

  if (!user || !["superadmin", "owner", "api_admin"].includes(user.role)) return null;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <DashboardHeader user={user} onLogout={handleLogout} />

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight mb-1">Hub</h1>
          <p className="text-sm text-muted-foreground">Internt administrationsværktøj</p>
        </div>

        {/* Fane-navigation */}
        <div className="flex gap-1 mb-6 border-b border-border">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors
                ${activeTab === id
                  ? "border-[#FF2A6D] text-[#FF2A6D]"
                  : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Indhold */}
        <div>
          {activeTab === "kalender" && <KalenderTab token={token} />}
          {activeTab === "feed"     && <FeedTab token={token} currentUser={user?.name} />}
          {activeTab === "koder"    && <KoderTab token={token} />}
          {activeTab === "filer"    && <DokumenterTab token={token} />}
        </div>
      </div>
    </div>
  );
}
